"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.addLeaveRequestCommentSchema = exports.leaveRequestDetailSchema = exports.leaveRequestPreviewSchema = exports.leaveRequestDaySchema = exports.leaveRequestCommentSchema = exports.leaveBalanceSchema = exports.leaveDecisionResultSchema = exports.decideLeaveRequestsSchema = exports.updateLeaveRequestSchema = exports.createLeaveRequestSchema = exports.leaveRequestSchema = exports.LEAVE_REQUEST_STATUSES = void 0;
const zod_1 = require("zod");
const identifier_1 = require("../identifier");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Leave requests — C47, Phase 3.                                            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **`days` IS COMPUTED, NEVER STORED, AND EVERY READER GETS THE SAME ANSWER BECAUSE OF IT.**
 * A request is a date range; what it costs is the WORKING days in that range, which C44's kernel
 * answers from the schedule in force and the workspace calendar. A week containing a public holiday
 * costs four days, not five. Freezing the number at submission would make a quota balance silently
 * wrong the day a holiday was imported.
 */
const wireDateSchema = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
exports.LEAVE_REQUEST_STATUSES = ['pending', 'approved', 'rejected', 'revoked'];
exports.leaveRequestSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** The public identifier, `leave_request_<8 hex>`, minted by the database (`0194`). */
    identifier: (0, identifier_1.identifierSchema)('leaveRequest'),
    /**
     * ⚠️⚠️ **`LV-1039` — WHAT A PERSON CALLS THIS REQUEST.** The mockup's every list leads with it
     * and ours had no such column, so a request could only be named by its dates. It leaves the
     * system — into a chat message, an email to HR — which is `0015`'s argument for task keys, one
     * table over. `0114` hands the numbers out.
     */
    code: zod_1.z.string(),
    memberId: zod_1.z.string(),
    memberName: zod_1.z.string(),
    /**
     * ⚠️ **C68 · what this person's generated face is drawn from.** The normalised email,
     * produced by `wireAvatarSeed`. This shape carries no avatar URL, which is why the first
     * pass missed it — it went looking for pictures and this row only ever had initials.
     */
    memberAvatarSeed: zod_1.z.string().nullable(),
    /** 28 Oct 2026 · their uploaded picture's path, when they have one — drawn over the seed's face. */
    memberAvatarUrl: zod_1.z.string().nullable(),
    leaveTypeId: zod_1.z.string(),
    leaveTypeName: zod_1.z.string(),
    year: zod_1.z.number().int(),
    fromDate: wireDateSchema,
    toDate: wireDateSchema,
    status: zod_1.z.enum(exports.LEAVE_REQUEST_STATUSES),
    note: zod_1.z.string().nullable(),
    /** ⚠️ Computed from C44. Whole days, and the reason this schema's header is what it is. */
    days: zod_1.z.number().int().nonnegative(),
    decidedByName: zod_1.z.string().nullable(),
    /** 27 Oct 2026 · the decider's face — the picture path, else the seed — so the column draws it. */
    decidedByAvatarUrl: zod_1.z.string().nullable(),
    decidedByAvatarSeed: zod_1.z.string().nullable(),
    decidedAt: zod_1.z.string().nullable(),
    decisionNote: zod_1.z.string().nullable(),
    /**
     * ⚠️⚠️ **WHETHER *THIS CALLER* MAY DECIDE IT, ANSWERED BY THE SERVER.** Being allowed to approve
     * is *the workspace permission **or** being this person's manager* — a rule the client cannot
     * evaluate without the whole org chart, and one it must not evaluate anyway. The screen draws the
     * buttons from this; the server checks again when they are pressed.
     */
    canDecide: zod_1.z.boolean(),
    /** Whether the caller may withdraw it — the requester, while it is still pending or approved. */
    canRevoke: zod_1.z.boolean(),
    /** When it was submitted — the list's *Beadva* column. */
    createdAt: zod_1.z.string(),
    version: zod_1.z.number().int(),
});
/**
 * 3 Nov 2026 · ⚠️ **A RANGE THAT ENDS BEFORE IT STARTS IS REFUSED HERE, ON `toDate`.** The
 * table's `leave_requests_range` check was the only guard, and its refusal reached the client as a
 * 500. ⚠️ A refinement, so it is applied to each schema after `.omit`/`.extend` — zod 4 refuses to
 * reshape an object that already carries one.
 */
function rangeInOrder(body, ctx) {
    if (body.toDate >= body.fromDate)
        return;
    ctx.addIssue({ code: 'custom', path: ['toDate'], message: 'ends before it starts' });
}
const leaveRequestBodySchema = zod_1.z.object({
    /**
     * ⚠️ **Absent means *me*.** Somebody filing for a colleague is an administrative act and needs
     * the same permission a decision does; the ordinary case is a person filing their own.
     */
    memberId: zod_1.z.string().optional(),
    leaveTypeId: zod_1.z.string(),
    fromDate: wireDateSchema,
    toDate: wireDateSchema,
    /** ⚠️ Absent means the year the range starts in — the service decides, not the form. */
    year: zod_1.z.number().int().min(2000).max(2100).optional(),
    note: zod_1.z.string().max(2000).nullable().optional(),
});
exports.createLeaveRequestSchema = leaveRequestBodySchema.superRefine(rangeInOrder);
exports.updateLeaveRequestSchema = leaveRequestBodySchema
    .omit({ memberId: true })
    .extend({ version: zod_1.z.number().int().nonnegative() })
    .superRefine(rangeInOrder);
/**
 * ⚠️⚠️ **A LIST, BECAUSE *Kérelmek* DECIDES OVER A SELECTION.** It is the only bulk-decision
 * surface in the product, and one request is a list of one — a separate single endpoint would be a
 * second path to the same rules, which is how the two drift.
 */
exports.decideLeaveRequestsSchema = zod_1.z.object({
    ids: zod_1.z.array(zod_1.z.string()).min(1).max(200),
    decision: zod_1.z.enum(['approved', 'rejected']),
    note: zod_1.z.string().max(2000).nullable().optional(),
    /**
     * ⚠️⚠️ **HOW AN APPROVED OVERTIME CLAIM SETTLES — feedback round 4 item 31.** Ignored by the
     * leave queue, which has no such choice, and ignored on a rejection, which settles nothing.
     *
     * ⚠️ **Absent means `payout`**, which is what approval has always done — so every existing
     * caller, the agent's tools included, keeps its behaviour without being edited.
     */
    settlement: zod_1.z.enum(['payout', 'credit']).optional(),
    /**
     * ⚠️ Which leave type a credit lands in. Absent takes the workspace's default
     * (`leave_types.accrues_overtime`), which is the product owner's *"a default the approver can
     * change"* — pre-selected, not fixed.
     */
    creditLeaveTypeId: zod_1.z.string().optional(),
    /**
     * ⚠️⚠️ **WHICH KIND OF OVERTIME, ON A PAYOUT — `0147`.** The requester says an hour was
     * overtime; the approver says what kind, and the rate freezes off that. Ignored by the leave
     * queue and by a credit, neither of which prices anything.
     *
     * ⚠️ **Absent takes what the CALENDAR suggests** — a holiday picks the holiday type, a weekend
     * the weekend one, anything else the workspace default. That is `0111`'s rule demoted from an
     * answer to an opening position, which is what keeps a bulk approval possible.
     */
    overtimeTypeId: zod_1.z.string().optional(),
});
/**
 * What a bulk decision did.
 *
 * ⚠️ **Per-id, because some of a selection can fail and the screen has to say which.** A request
 * somebody else decided while the list was open is `refused`, not an error for the whole batch —
 * the same shape `bulk.ts` established for the task lists.
 */
exports.leaveDecisionResultSchema = zod_1.z.object({
    decided: zod_1.z.array(zod_1.z.string()),
    refused: zod_1.z.array(zod_1.z.object({ id: zod_1.z.string(), code: zod_1.z.string() })),
});
/**
 * One person's balance of one type in one year — the *Kvóta / Felhasználva* pair.
 *
 * ⚠️ **`pending` is counted separately from `used`**, because a request awaiting a decision is not
 * spent and is not free either. The mockup shows both, and a balance that hid the pending days
 * would let somebody book the same fortnight twice.
 */
exports.leaveBalanceSchema = zod_1.z.object({
    leaveTypeId: zod_1.z.string(),
    leaveTypeName: zod_1.z.string(),
    year: zod_1.z.number().int(),
    /** ⚠️ `null` for an unlimited type, which has no frame rather than a frame of zero. */
    quotaDays: zod_1.z.number().int().nullable(),
    usedDays: zod_1.z.number().int().nonnegative(),
    pendingDays: zod_1.z.number().int().nonnegative(),
    /**
     * Days moved here from last year by the carry-over chore — P3-8.
     *
     * ⚠️ **Zero once they have expired, and the date goes with them.** The server does that filtering
     * rather than the screen: *"5 carried days, expired in March"* is a number a reader will add to
     * their balance, and the balance no longer contains it.
     */
    carriedDays: zod_1.z.number().int().nonnegative(),
    carriedExpiresOn: zod_1.z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable(),
    /**
     * ┌────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ DAYS EARNED BY OVERTIME — feedback round 4 item 25.                        │
     * └────────────────────────────────────────────────────────────────────┘
     *
     * ⚠️⚠️ **TWO FIGURES, AND THE DIFFERENCE IS THE ITEM.** `accruedDays` is what has been earned,
     * in parts of a day — *"1.5 days of accrued overtime"*. `accruedTakeable` is what may actually be
     * taken, which is that rounded DOWN — *"yields 1 day"*. A screen showing only the first would
     * offer half a day nobody can book; showing only the second would lose the half that is waiting
     * for its other half.
     *
     * ⚠️ **`quotaDays` already includes the takeable part**, so a reader who adds them double-counts.
     * These are the breakdown, exactly as `carriedDays` is.
     *
     * ⚠️ **It resets each new year** — the product owner's ruling, 18 Sep. There is no carry-over
     * and no expiry date: a new year simply starts at zero.
     */
    accruedDays: zod_1.z.string(),
    accruedTakeable: zod_1.z.number().int().nonnegative(),
});
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ P3-9 · one request, in full — the mockup's `leavereq` screen.             │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **The list shape plus what only a detail can afford.** A queue row shows a range and a status;
 * this adds the conversation and the rules the request was made under, which is what makes the
 * screen worth its route rather than a wider row.
 */
exports.leaveRequestCommentSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** The public identifier, `leave_comment_<8 hex>`, minted by the database (`0194`). */
    identifier: (0, identifier_1.identifierSchema)('leaveComment'),
    authorMemberId: zod_1.z.string(),
    authorName: zod_1.z.string(),
    /**
     * ⚠️ **C68 · what this person's generated face is drawn from.** The normalised email,
     * produced by `wireAvatarSeed`. This shape carries no avatar URL, which is why the first
     * pass missed it — it went looking for pictures and this row only ever had initials.
     */
    authorAvatarSeed: zod_1.z.string().nullable(),
    /** 28 Oct 2026 · their uploaded picture's path, when they have one — drawn over the seed's face. */
    authorAvatarUrl: zod_1.z.string().nullable(),
    /**
     * ⚠️⚠️ **HTML, SANITISED ON THE WAY IN — `0119`.** It was `body`, one line of plain text
     * drawn with `whitespace-pre-wrap`, while a task comment has been rich text since C19. Two
     * comment boxes in one product that behave differently is a difference nobody can explain to the
     * person typing in them.
     *
     * ⚠️ **The name is the contract.** A `body` that silently began holding markup would leave
     * every reader guessing which it had, and a reader that guessed wrong prints tags at somebody.
     */
    bodyHtml: zod_1.z.string(),
    createdAt: zod_1.z.string(),
    /** ⚠️ `null` until somebody edits it — the same fact *szerkesztve* is rendered from. */
    editedAt: zod_1.z.string().nullable(),
});
/**
 * ⚠️⚠️ **THE DAYS THEMSELVES, NOT JUST HOW MANY.** The mockup's request detail lists every working
 * day the range costs with the hours it was worth — `hét · szept 7. 8:00` — which is the only place
 * in the product that shows WHY five calendar days cost three. A count alone leaves somebody who
 * disagrees with it nothing to check.
 */
exports.leaveRequestDaySchema = zod_1.z.object({
    date: zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** The plan for that day, `H:MM`. A day the range covers but nobody works is not in this list. */
    hours: zod_1.z.string(),
});
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ WHAT A RANGE WOULD COST, BEFORE ANYTHING IS SUBMITTED.                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The mockup's new-request dialog says *"a kvótád élőben követi a kijelölést"* — the day count and
 * the balance move as somebody drags across the calendar. That figure is C44's kernel applied to
 * the caller's own schedule and the workspace's calendar, which no client can compute: it would
 * need the schedule timeline, the holiday table and the leave already approved.
 *
 * ⚠️ **A READ, not a dry-run write.** It creates nothing and refuses nothing — the refusals stay on
 * `POST`, where they are enforced. This only answers *how many working days is this*.
 */
exports.leaveRequestPreviewSchema = zod_1.z.object({
    days: zod_1.z.number().int().nonnegative(),
    dayBreakdown: zod_1.z.array(exports.leaveRequestDaySchema),
    /** Which days in the range are NOT worked, so the calendar can strike them through. */
    offDays: zod_1.z.array(zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/)),
});
exports.leaveRequestDetailSchema = exports.leaveRequestSchema.extend({
    dayBreakdown: zod_1.z.array(exports.leaveRequestDaySchema),
    /**
     * The requester's balance for THIS type and frame year, so the rail can draw the same bar the
     * request will move. ⚠️ `null` for a type with no frame — unlimited is not a quota of zero.
     */
    balance: zod_1.z
        .object({
        quotaDays: zod_1.z.number().int(),
        usedDays: zod_1.z.number().int().nonnegative(),
        pendingDays: zod_1.z.number().int().nonnegative(),
    })
        .nullable(),
    comments: zod_1.z.array(exports.leaveRequestCommentSchema),
    /**
     * The type's own rules, carried on the request rather than fetched beside it — ⚠️ **a screen that
     * had to load the type list to know whether a certificate is required would show the wrong answer
     * for the first paint, which is the paint a decider acts on.**
     */
    attachmentRule: zod_1.z.enum(['optional', 'required']),
    noteRule: zod_1.z.enum(['optional', 'required', 'hidden']),
    /**
     * ⚠️ **Anybody who can SEE the request can answer on it.** The thread is how a decider asks a
     * question, and a requester who could not reply would leave the decider waiting on a screen that
     * offered them nothing to do.
     */
    canComment: zod_1.z.boolean(),
});
/** ⚠️ Plain text, and trimmed before the length check — a comment of four spaces is not a comment. */
exports.addLeaveRequestCommentSchema = zod_1.z.object({
    /* ⚠️ 20000, the task comment's budget — the same prose carries its own markup now, and a
       limit that counted tags against what somebody wrote would refuse a comment for being
       formatted. The server sanitises and then refuses an empty result. */
    bodyHtml: zod_1.z.string().trim().min(1).max(20000),
});
//# sourceMappingURL=leave-request.js.map